import { Store, RootGetters } from 'stores/type';
import { StorageKey } from 'constants/storage';
import { save, load } from 'helpers/storage';
import { EventsWithoutPayload } from 'types';
import EventsManager from 'managers/events.manager';
import ENTRIES from './entries';

export interface StorageEntry<Parsed, Saved> {
  key: StorageKey,
  parse(data: Saved): Parsed,
  prepare($store: Store): Saved,
  watch: Array<keyof RootGetters>,
}

const SAVE_EVENTS: EventsWithoutPayload[] = [
  'UNLOAD',
  'VISIBILITY_HIDDEN',
];

class StorageManager {
  private $store!: Store;
  private entries: Record<StorageKey, StorageEntry<any, any>>;

  constructor(entries: Record<StorageKey, StorageEntry<any, any>>) {
    this.entries = entries;
  }

  public init(store: Store) {
    this.$store = store;
    this.initSaveTriggers();
  }
  private initSaveTriggers() {
    SAVE_EVENTS.forEach((event) => EventsManager.on(event, this.save));

    Object.values(this.entries)
      .forEach(({ watch }) =>
        watch.forEach((getter) =>
          this.$store.watch(
            (_s, getters) => getters[getter],
            this.save,
          ),
        ),
      );
  }

  public save = () => {
    Object.values(this.entries).forEach((entry) => {
      const data = entry.prepare(this.$store);
      this.saveSingle(entry.key, data);
    });
  }
  private saveSingle(key: StorageKey, data: any) {
    save(key, data);
  }

  public load<T extends StorageKey>(key: T): ReturnType<typeof ENTRIES[T]['parse']> | null {
    const data = load(key);
    if (!data) return data;

    const entry = this.getEntryByKey(key) as any;
    if (!entry) return data;

    return entry.parse(data);
  }

  public disable() {
    SAVE_EVENTS.forEach((event) => EventsManager.off(event, this.save));
  }
  public clear() {
    this.disable();
    localStorage.clear();
  }

  private getEntryByKey(key: StorageKey) {
    return this.entries[key] || null;
  }
}

const manager = new StorageManager(ENTRIES);

if (__DEV__ || __STAGING__) {
  window.__DISABLE_STORAGE = () => manager.disable();
  window.__CLEAR_STORAGE = () => manager.clear();
}

export { StorageKey };
export default manager;
